import { carveExpansion } from './expansion-world.js';
import { B } from "./blocks.js";
import { Gen } from "./levelgen.js";
import { THEMES } from "./themes.js";
import { RNG, hash2 } from "./util.js";
import {
  REGION,
  homeRegion,
  regionKey,
  biomeAt,
  groundHeight,
  onTrail,
  wildStation,
} from "./discovery-terrain.js";
import { STATIONS, PLOT, STALL } from "./discovery-state.js";

function decorateHome(world) {
  const gen = new Gen(world, THEMES.plains, new RNG(8303));

  // Wide, level trails: every learning station can be reached by walking.
  for (const s of STATIONS) {
    const n = Math.ceil(Math.hypot(s.x, s.z));
    for (let i = 0; i <= n; i++) {
      const x = Math.round((s.x * i) / n),
        z = Math.round((s.z * i) / n);
      world.fill(x - 1, 40, z - 1, x + 1, 40, z + 1, B.PATH);
    }
    world.fill(s.x - 3, 40, s.z - 3, s.x + 3, 40, s.z + 3, B.STONE_BRICK);
  }
  world.fill(
    PLOT.x0 - 1,
    40,
    PLOT.z0 - 1,
    PLOT.x1 + 1,
    40,
    PLOT.z1 + 1,
    B.PLANKS,
  );
  for (let x = PLOT.x0 - 1; x <= PLOT.x1 + 1; x++)
    for (const z of [PLOT.z0 - 1, PLOT.z1 + 1])
      world.set(x, 40, z, B.GOLD_BLOCK);
  for (let z = PLOT.z0 - 1; z <= PLOT.z1 + 1; z++)
    for (const x of [PLOT.x0 - 1, PLOT.x1 + 1])
      world.set(x, 40, z, B.GOLD_BLOCK);
  // Voxel landmarks, kept behind the learning pedestals.
  gen.tree(-16, 41, 1, "oak");
  gen.tree(-17, 41, 8, "birch");
  gen.tree(19, 41, 6, "oak");
  world.fill(-15, 41, 16, -9, 43, 19, B.PLANKS);
  world.fill(-14, 41, 15, -10, 42, 15, B.GLASS);
  world.fill(-16, 44, 15, -8, 44, 20, B.BRICK);
  for (const x of [-3, 0, 3])
    world.fill(x, 41, 23, x, 42 + Math.abs(x), 23, B.CRYSTAL);
  for (let x = 9; x <= 15; x += 2)
    for (let z = 16; z <= 18; z++) world.set(x, 41, z, B.PUMPKIN);
  world.fill(16, 41, -5, 18, 47, -3, B.STONE_BRICK);
  world.fill(15, 48, -6, 19, 48, -2, B.GOLD_BLOCK);
  world.set(17, 49, -4, B.GLOWSTONE);
  world.fill(-2, 41, -17, 2, 41, -15, B.BRICK);
  for (const [x, z] of [
    [-20, -6],
    [20, 15],
    [-8, -21],
    [9, 25],
    [-20, 18],
  ])
    gen.tree(x, 41, z, "oak");
  // A tiny pond and flowers, outside all learning paths and the plot.
  world.fill(-20, 40, -15, -16, 40, -11, B.WATER);
  for (const [x, z] of [
    [-19, -9],
    [18, 10],
    [-6, 22],
    [8, -15],
  ])
    world.set(x, 41, z, B.FLOWER_RED);
  // A real cottage doorway, roof ridge, books and flower boxes.
  world.fill(-13, 41, 15, -12, 42, 19, B.AIR);
  world.fill(-14, 41, 16, -10, 42, 18, B.AIR);
  world.fill(-15, 41, 19, -9, 42, 19, B.BOOKSHELF);
  world.fill(-15, 45, 16, -9, 45, 19, B.BRICK);
  world.fill(-14, 46, 17, -10, 46, 18, B.BRICK);
  world.set(-14, 41, 14, B.FLOWER_RED);
  world.set(-10, 41, 14, B.FLOWER_YELLOW);
  // Crystal arch and a stepped workshop roof make destinations recognisable.
  world.fill(-3, 46, 23, 3, 46, 23, B.CRYSTAL);
  world.fill(-3, 41, -18, -3, 44, -18, B.LOG);
  world.fill(3, 41, -18, 3, 44, -18, B.LOG);
  world.fill(-4, 45, -19, 4, 45, -15, B.PLANKS);
  world.set(-2, 42, -17, B.BOOKSHELF);
  world.set(2, 42, -17, B.LAMP);
  // A clearly marked renewable gathering grove, separate from protected town landmarks.
  gen.tree(10, 41, -20, "oak");
  for (const [x, z, id] of [
    [9, -12, B.COBBLE],
    [10, -12, B.COBBLE],
    [9, -13, B.COAL_ORE],
    [12, -13, B.SAND],
    [13, -13, B.SAND],
    [13, -22, B.IRON_ORE],
  ])
    world.set(x, 41, z, id);
  // The Craft & Trade stall: the public crafting table beside the pond, well away from every learning gem.
  const { x: sx, y: sy, z: sz } = STALL;
  world.fill(sx - 2, 40, sz - 2, sx + 2, 40, sz + 2, B.STONE_BRICK);
  for (const dx of [-2, 2]) {
    world.fill(sx + dx, sy, sz - 2, sx + dx, sy + 2, sz - 2, B.LOG);
    world.set(sx + dx, sy + 3, sz - 2, B.LAMP);
  }
  for (const dx of [-1, 1]) world.set(sx + dx, sy, sz - 2, B.BOOKSHELF);
  world.set(sx, sy, sz, B.CRAFTING_TABLE);
  // Lantern-lined exits continue beyond the town in every direction.
  for (let n = -31; n <= 31; n++)
    if (Math.abs(n) > 25) {
      world.fill(n, 40, -1, n, 40, 1, B.PATH);
      world.fill(-1, 40, n, 1, 40, n, B.PATH);
    }
  for (const [x, z] of [
    [-26, 3],
    [26, -3],
    [3, 27],
    [-3, -26],
  ]) {
    world.fill(x, 41, z, x, 43, z, B.LOG);
    world.set(x, 44, z, B.LAMP);
  }
}

// Clip decoration writes to one region so generation order cannot alter neighbours.
function clipped(world, x0, z0) {
  const inside = (x, z) =>
    x >= x0 && x < x0 + REGION && z >= z0 && z < z0 + REGION;
  const view = {
    get: (x, y, z) => world.get(x, y, z),
    set: (x, y, z, id) => {
      if (inside(x, z)) world.set(x, y, z, id);
    },
    place: (x, y, z, id) => {
      if (inside(x, z) && !world.get(x, y, z)) world.set(x, y, z, id);
    },
  };
  view.fill = (x0, y0, z0, x1, y1, z1, id) => {
    for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++)
      for (let z = Math.min(z0, z1); z <= Math.max(z0, z1); z++)
        if (inside(x, z))
          for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++)
            view.set(x, y, z, id);
  };
  return view;
}
export function generateRegion(world, rx, rz) {
  const x0 = rx * REGION,
    z0 = rz * REGION,
    home = homeRegion(rx, rz);
  const biome = biomeAt(x0 + 16, z0 + 16),
    w = clipped(world, x0, z0);
  for (let x = x0; x < x0 + REGION; x++)
    for (let z = z0; z < z0 + REGION; z++) {
      const h = groundHeight(x, z);
      w.fill(
        x,
        8,
        z,
        x,
        h - 1,
        z,
        B.STONE,
      );
      w.fill(x, h - 2, z, x, h - 1, z, biome.id === "desert" ? B.SANDSTONE : B.DIRT);
      w.set(x, h, z, !home && onTrail(x, z) ? B.PATH : biome.surface);
      if (!home && !onTrail(x, z) && hash2(x, z, 41) > 0.94)
        w.set(x, h + 1, z, biome.plant);
    }
  if (home) {
    decorateHome(w);
    return;
  }
  const gen = new Gen(
    w,
    THEMES[
      biome.id === "crystal"
        ? "glitch"
        : biome.id === "meadow"
          ? "plains"
          : biome.id
    ],
    new RNG(Math.floor(hash2(rx, rz, 12) * 1e8)),
  );
  // Trees stay clear of paths, station approaches and region seams.
  for (const [lx, lz] of [
    [6, 6],
    [25, 7],
    [7, 25],
    [25, 25],
  ]) {
    const x = x0 + lx,
      z = z0 + lz;
    gen.tree(x, groundHeight(x, z) + 1, z, biome.tree);
  }
  // Surface seams can be mined without digging dangerous holes in the walking terrain.
  for (const [lx, lz, id] of [
    [10, 22, B.COBBLE],
    [11, 22, B.COBBLE],
    [10, 23, B.COAL_ORE],
    [12, 23, B.IRON_ORE],
    [23, 12, B.SAND],
    [
      24,
      12,
      biome.id === "snow" || biome.id === "desert"
        ? B.DIAMOND_ORE
        : biome.id === "crystal"
          ? B.CRYSTAL
          : B.COAL_ORE,
    ],
  ]) {
    const x = x0 + lx,
      z = z0 + lz;
    w.set(x, groundHeight(x, z) + 1, z, id);
  }
  const cx = x0 + 16,
    cz = z0 + 16;
  w.fill(cx - 2, 40, cz - 2, cx + 2, 40, cz + 2, B.STONE_BRICK);
  // A little landmark beside every field lesson.
  if (biome.id === "desert") {
    for (let i = 0; i < 4; i++)
      w.fill(
        x0 + 22 + i,
        groundHeight(x0 + 25, z0 + 23) + 1 + i,
        z0 + 20 + i,
        x0 + 28 - i,
        groundHeight(x0 + 25, z0 + 23) + 1 + i,
        z0 + 26 - i,
        B.SANDSTONE,
      );
  } else if (biome.id === "crystal") {
    for (let i = 0; i < 4; i++)
      w.fill(x0 + 21 + i, 41, z0 + 10, x0 + 21 + i, 44 + i, z0 + 10, B.CRYSTAL);
  } else if (biome.id === "snow") {
    w.fill(
      x0 + 9,
      groundHeight(x0 + 9, z0 + 10) + 1,
      z0 + 10,
      x0 + 10,
      groundHeight(x0 + 9, z0 + 10) + 3,
      z0 + 11,
      B.SNOW,
    );
    w.set(x0 + 9, groundHeight(x0 + 9, z0 + 10) + 4, z0 + 10, B.PUMPKIN);
  } else {
    for (let x = x0 + 21; x < x0 + 26; x++)
      for (let z = z0 + 10; z < z0 + 13; z++) {
        w.set(x, groundHeight(x, z), z, B.WATER);
        w.set(x, groundHeight(x, z) - 1, z, B.SAND);
      }
  }
  carveExpansion(w,rx,rz);
}
export function generateDiscovery(world) {
  for (let rx = -1; rx <= 0; rx++)
    for (let rz = -1; rz <= 0; rz++) generateRegion(world, rx, rz);
  return { spawn: { x: 0.5, y: 41, z: -3.5, yaw: 0 }, killY: 20 };
}

// Explore's fog, as flat distance from the player (game/fog.js): clear for FOG_CLEAR blocks all round, fading over
// FOG_FADE. The stream keeps two regions loaded past the player's own, at least 2 × REGION = 64 blocks along x and z,
// so the fog is complete before the unloaded edge in every direction, whatever the camera does.
export const FOG_CLEAR = 38, FOG_FADE = 24;
export const EXPLORE_FOG = { near: FOG_CLEAR, far: FOG_CLEAR + FOG_FADE };
// The loaded world around region (rx, rz): that region and two more every way.
export const streamBounds = (rx, rz) => ({ x0: (rx - 2) * REGION, x1: (rx + 3) * REGION, z0: (rz - 2) * REGION, z1: (rz + 3) * REGION });

// Only nearby terrain stays in memory. New regions grow ahead of the player;
// the inner ring is synchronous so walking or returning Home never finds a hole.
export class DiscoveryStream {
  constructor(world, blocks = [], onAdd = () => {}, onRemove = () => {}) {
    this.world = world;
    this.blocks = blocks;
    this.loaded = new Map();
    this.queue = [];
    this.onAdd = onAdd;
    this.onRemove = onRemove;
    this.centre = "";
  }
  add(rx, rz) {
    const key = regionKey(rx, rz);
    if (this.loaded.has(key)) return;
    generateRegion(this.world, rx, rz);
    for (const b of this.blocks)
      if (Math.floor(b[0] / REGION) === rx && Math.floor(b[2] / REGION) === rz)
        this.world.set(b[0], b[1], b[2], b[3]);
    this.loaded.set(key, { rx, rz });
    this.onAdd(rx, rz, wildStation(rx, rz));
    this.world.dirtyRegion(rx * REGION, rz * REGION, REGION);
  }
  update(x, z, immediate = false) {
    const rx = Math.floor(x / REGION),
      rz = Math.floor(z / REGION),
      key = regionKey(rx, rz);
    if (this.centre !== key) {
      this.centre = key;
      this.queue = [];
      for (const [k, r] of this.loaded)
        if (Math.abs(r.rx - rx) > 2 || Math.abs(r.rz - rz) > 2) {
          this.world.unloadRegion(r.rx * REGION, r.rz * REGION, REGION);
          this.loaded.delete(k);
          this.onRemove(r.rx, r.rz);
        }
      for (let dx = -2; dx <= 2; dx++)
        for (let dz = -2; dz <= 2; dz++)
          if (!this.loaded.has(regionKey(rx + dx, rz + dz)))
            this.queue.push({ rx: rx + dx, rz: rz + dz, d: dx * dx + dz * dz });
      this.queue.sort((a, b) => a.d - b.d);
    }
    const urgent = this.queue.filter(
      (r) => Math.abs(r.rx - rx) <= 1 && Math.abs(r.rz - rz) <= 1,
    );
    for (const r of urgent) this.add(r.rx, r.rz);
    this.queue = this.queue.filter(
      (r) => !this.loaded.has(regionKey(r.rx, r.rz)),
    );
    const next = immediate ? this.queue.splice(0) : this.queue.splice(0, 1);
    for (const r of next) this.add(r.rx, r.rz);
    this.world.rebuild();
  }
}
